import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import Footer from '@/components/common/Footer';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { GlobalAccountModal } from '@/components/account/GlobalAccountModal';
import { useAuthStore } from '@/lib/stores/authStore';
import { getVideoUrl, getModuleHeroVideoUrl } from '@/lib/media';

const BG_VIDEOS = [
  getModuleHeroVideoUrl('goldvales_last_hope'),
  getVideoUrl('tavian.mp4'),
  getVideoUrl('aldric.mp4'),
  getVideoUrl('kira.mp4'),
  getVideoUrl('grolt.mp4'),
];

const ROTATE_INTERVAL = 20_000;
const FADE_DURATION = 2000;

function pickRandom(exclude: number) {
  let next: number;
  do {
    next = Math.floor(Math.random() * BG_VIDEOS.length);
  } while (next === exclude && BG_VIDEOS.length > 1);
  return next;
}

const initialIndex = 0; // Always start with goldvale hero

export default function PublicLayout() {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarExpanded, setIsDesktopSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { pathname } = useLocation();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  // Logged-in user on `/` is about to be redirected to /dashboard; skip
  // rendering the heavy video stack so we don't briefly load goldvales just
  // to throw it away.
  const skipVideo = isLoggedIn && pathname === '/';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const [srcA] = useState(BG_VIDEOS[initialIndex]);
  const [srcB] = useState(BG_VIDEOS[pickRandom(initialIndex)]);
  const [showA, setShowA] = useState(true);
  const showARef = useRef(true);
  const lastIndexRef = useRef(initialIndex);
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);

  // Rotate videos
  useEffect(() => {
    // A `canplaythrough` that never fires (slow network) must not leave its
    // listener behind: the next tick would stack another, and each stale
    // one still flips showA when its slow load finally lands.
    let removePendingReady: (() => void) | null = null;
    let pauseTimer: number | undefined;

    const timer = setInterval(() => {
      const nextIndex = pickRandom(lastIndexRef.current);
      lastIndexRef.current = nextIndex;

      const hiddenVideo = showARef.current ? videoBRef.current : videoARef.current;
      if (!hiddenVideo) return;

      removePendingReady?.();

      // Load new source into hidden slot
      hiddenVideo.src = BG_VIDEOS[nextIndex];
      hiddenVideo.load();

      const onReady = () => {
        removePendingReady = null;
        hiddenVideo.removeEventListener('canplaythrough', onReady);
        hiddenVideo.play().catch(() => {});
        // An opacity-0 slot still decodes every frame and starves the GPU
        // (blank content tiles while scrolling), so pause the outgoing slot
        // once the crossfade has finished. It resumes via play() on its next
        // turn.
        const outgoing = showARef.current ? videoARef.current : videoBRef.current;
        showARef.current = !showARef.current;
        setShowA(showARef.current);
        window.clearTimeout(pauseTimer);
        pauseTimer = window.setTimeout(() => outgoing?.pause(), FADE_DURATION);
      };
      hiddenVideo.addEventListener('canplaythrough', onReady);
      removePendingReady = () => hiddenVideo.removeEventListener('canplaythrough', onReady);
    }, ROTATE_INTERVAL);

    return () => {
      clearInterval(timer);
      removePendingReady?.();
      window.clearTimeout(pauseTimer);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Release both slot decoders whenever the video subtree leaves (skipVideo
  // flip or route unmount). pause() alone keeps the demuxer + decoded frames
  // alive on the detached elements. Elements are captured at setup because
  // React nulls refs before effect cleanups run; the release is deferred one
  // tick and gated on the element actually being out of the DOM, so
  // StrictMode's dev remount (cleanup on still-live elements) is a no-op.
  useEffect(() => {
    if (skipVideo) return;
    const slots = [videoARef.current, videoBRef.current];
    return () => {
      window.setTimeout(() => {
        for (const v of slots) {
          if (v && !v.isConnected) {
            v.pause();
            v.removeAttribute('src');
            v.load();
          }
        }
      }, 0);
    };
  }, [skipVideo]);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased flex relative">
      {!skipVideo && (
        <>
          {/* Video background — two slots for crossfade */}
          <video
            ref={videoARef}
            src={srcA}
            className="fixed inset-0 h-full w-full object-cover z-[1]"
            style={{ opacity: showA ? 1 : 0, transition: `opacity ${FADE_DURATION}ms ease-in-out` }}
            muted
            autoPlay
            loop
            playsInline
          />
          {/* No autoPlay: slot B starts hidden and only plays when the rotate
              swap fades it in; an invisible playing video still burns decode. */}
          <video
            ref={videoBRef}
            src={srcB}
            className="fixed inset-0 h-full w-full object-cover z-[1]"
            style={{ opacity: showA ? 0 : 1, transition: `opacity ${FADE_DURATION}ms ease-in-out` }}
            muted
            loop
            playsInline
          />
        </>
      )}

      {/* Overlay for text legibility */}
      {!skipVideo && (
        <div className="fixed inset-0 z-[2] pointer-events-none bg-gradient-to-b from-background/70 via-background/50 to-background/70" />
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <Sidebar
          isOpen={isMobileSidebarOpen}
          isDesktopExpanded={false}
          setIsOpen={setIsMobileSidebarOpen}
          isMobile={true}
        />
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar
          isOpen={false}
          isDesktopExpanded={isDesktopSidebarExpanded}
          setIsOpen={setIsDesktopSidebarExpanded}
          isMobile={false}
        />
      )}

      <div
        className={`relative z-[3] flex-1 flex flex-col transition-all duration-250 ease-in-out ${!isMobile ? 'ml-16' : ''}`}
      >
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="fixed top-3 left-3 z-40 bg-background/80 backdrop-blur-sm shadow-sm border lg:hidden"
            onClick={() => setIsMobileSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        <main className="flex-1">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
      {/* Account modal for logged-in users viewing a public route (the sidebar
          UserMenu's Account item + any deep-link CTA). RootLayout mounts its
          own copy; only one layout is active per route, so they never coexist. */}
      {isLoggedIn && <GlobalAccountModal />}
    </div>
  );
}
